import { useEffect, useSyncExternalStore } from "react";
import {
  fetchMyNotifications,
  markMyNotificationsRead,
  type NotificationItem,
} from "../organizer/api";

/**
 * EL FEED DE AVISOS DEL JUGADOR.
 *
 * POR QUÉ EXISTE (03-11-2026). `notifications` se escribe para el jugador
 * desde hace meses —foto rechazada con su motivo, foto que entró a la ficha,
 * tu solicitud aprobada o rechazada, tu ficha que pasó a otra cuenta, te
 * comentaron la publicación, invitación a organizar, partido reprogramado,
 * suspensión puesta o levantada— y **nadie la leía de su lado**:
 * `/me/notifications` lo consumía únicamente el organizador, en su Actividad.
 * El único canal real era la push, que es de un solo intento.
 *
 * El endpoint, el marcar-leído, la poda y el agrupado ya estaban. Faltaba la
 * pantalla. Ver `docs/PROPUESTA_PENDIENTES_DEL_JUGADOR.md`, pieza B.
 *
 * LA REGLA que lo separa de los pendientes: acá va lo que **ya pasó** y se va
 * cuando se lee. Lo que **espera que vos hagas** se calcula del dato y vive
 * como tarjeta en el Inicio (ver `solicitudesQueEsperan.ts`).
 *
 * Gemelo de `features/jugador/AvisosStore.kt`. El import sale de
 * `organizer/api` a propósito: es el MISMO endpoint y el mismo tipo, y
 * duplicarlo acá sería el primer paso para que los dos lados se separen.
 */

/**
 * LO QUE NO ENTRA AL FEED: los tipos que ya tienen su propia puerta con
 * globito **que se ve desde cualquier contexto**. No es una lista de
 * excepciones, es la regla 4.4 aplicada — **lo que espera que VOS hagas es un
 * pendiente y vive donde se hace; al feed va lo que ya pasó**. Un pendiente
 * acá se podría "marcar leído" y dejaría de verse sin haberse hecho.
 *
 *  · `internal_message`  → fila "Mensajes con FORJA", que está siempre.
 *  · `team_join_request` → tarjeta en el Inicio (ámbito por ROLES) y globito
 *    en "Mi equipo".
 *
 * LAS FOTOS PARA REVISAR NO ESTÁN ACÁ, y es la corrección de una que me mandé
 * el 03-11-2026: las saqué pensando que la baldosa "Fotos del plantel" las
 * cubría, pero esa baldosa sólo aparece si sos líder del equipo DEL CONTEXTO
 * ACTIVO. Visto en device con una cuenta parada en otra liga, el aviso
 * desaparecía de todos lados. Hasta que las fotos tengan su pendiente por
 * roles (pieza C), el feed es el único lugar donde se ven desde cualquier
 * contexto.
 */
const TIPOS_QUE_NO_VAN = new Set(["internal_message", "team_join_request"]);

let avisos: NotificationItem[] = [];
let sinLeer = 0;
let ultimoMs = 0;
let pidiendo = false;
const oyentes = new Set<() => void>();

const TTL_MS = 5 * 60_000;
const VACIO: NotificationItem[] = [];

function avisar() {
  for (const oyente of oyentes) oyente();
}

/** Logout: que la próxima cuenta no herede los avisos de la anterior. */
export function resetAvisos() {
  if (avisos.length === 0 && sinLeer === 0 && ultimoMs === 0) return;
  avisos = VACIO;
  sinLeer = 0;
  ultimoMs = 0;
  avisar();
}

/**
 * Pregunta al server y publica. Con TTL porque lo llama cada pantalla que
 * muestra el número; se traga el error a propósito — lo último que supimos es
 * mejor que un cero mentiroso.
 */
export async function refrescarAvisos(forzar = false) {
  const ahora = Date.now();
  if (!forzar && (pidiendo || ahora - ultimoMs < TTL_MS)) return;
  pidiendo = true;
  try {
    const respuesta = await fetchMyNotifications();
    const lista = (respuesta.notifications ?? []).filter((n) => !TIPOS_QUE_NO_VAN.has(n.typeCode));
    avisos = lista.length === 0 ? VACIO : lista;
    sinLeer = lista.filter((n) => n.readAt === null).length;
    ultimoMs = Date.now();
    avisar();
  } catch {
    // Sin red, sin sesión, lo que sea: queda lo último conocido.
  } finally {
    pidiendo = false;
  }
}

/**
 * Marca todo leído: lo llama la hoja al abrirse, igual que la Actividad del
 * organizador. El contador baja en el acto y no al volver el server, porque
 * el globito tiene que apagarse cuando el jugador MIRA; si el POST falla, el
 * próximo refresco lo repone.
 */
export async function marcarAvisosLeidos() {
  if (sinLeer === 0) return;
  sinLeer = 0;
  avisar();
  try {
    await markMyNotificationsRead();
  } catch {
    // Lo repone el próximo refrescarAvisos().
  }
}

/** Sólo el número, para el globito de la fila del cajón. */
export function useAvisosSinLeer(): number {
  const valor = useSyncExternalStore(
    (alCambiar) => {
      oyentes.add(alCambiar);
      return () => {
        oyentes.delete(alCambiar);
      };
    },
    () => sinLeer,
    () => 0,
  );

  useEffect(() => {
    void refrescarAvisos();
    const alVolver = () => {
      if (document.visibilityState === "visible") void refrescarAvisos(true);
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, []);

  return valor;
}

/** La lista, para la hoja. */
export function useAvisos(): NotificationItem[] {
  return useSyncExternalStore(
    (alCambiar) => {
      oyentes.add(alCambiar);
      return () => {
        oyentes.delete(alCambiar);
      };
    },
    () => avisos,
    () => VACIO,
  );
}
